import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Camera, ArrowRight } from 'lucide-react';
import { useGalleryConfig } from '../context/GalleryConfigContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import { GalleryEvent } from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';
import Button from '../components/common/Button';
import LoadingScreen from '../components/common/LoadingScreen';

const GalleryHomePage: React.FC = () => {
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();
  const [featuredEvents, setFeaturedEvents] = useState<GalleryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFeaturedEvents = async () => {
      if (!config?.photographerId) return;

      try {
        setLoading(true);
        setError(null);

        // Track page view
        await galleryApiService.trackEvent(config.photographerId, {
          type: 'page_view',
          metadata: { page: 'home' },
        });

        // Get recent/featured events
        const eventsResponse = await galleryApiService.getEvents(config.photographerId, {
          sortBy: 'created',
          sortOrder: 'desc',
        });

        setFeaturedEvents(eventsResponse.items.slice(0, 6)); // Show up to 6 events
      } catch (err) {
        setError('Failed to load galleries');
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedEvents();
  }, [config]);

  if (loading) {
    return <LoadingScreen message="Loading gallery..." />;
  }

  if (!config) {
    return <div>Gallery configuration not found</div>;
  }

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <section className="text-center py-12">
        <div className="max-w-4xl mx-auto">
          <h1
            className="text-4xl md:text-6xl font-bold mb-6"
            style={{ color: theme.colors.text.primary }}
          >
            {config.businessName}
          </h1>
          {config.seo.description && (
            <p
              className="text-xl md:text-2xl mb-8 leading-relaxed"
              style={{ color: theme.colors.text.secondary }}
            >
              {config.seo.description}
            </p>
          )}
          <Link to="/events">
            <Button
              size="lg"
              className="inline-flex items-center space-x-2"
              rightIcon={<ArrowRight size={20} />}
            >
              <span>Browse Galleries</span>
            </Button>
          </Link>
        </div>
      </section>

      {/* Featured Events Grid */}
      {featuredEvents.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold" style={{ color: theme.colors.text.primary }}>
              Recent Galleries
            </h2>
            <Link to="/events">
              <Button variant="ghost" rightIcon={<ArrowRight size={16} />}>
                View All
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredEvents.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {/* About Section */}
      {config.settings.showPhotographer && (
        <section
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: theme.colors.surface }}
        >
          <div className="max-w-2xl mx-auto">
            <Camera size={48} className="mx-auto mb-4" style={{ color: theme.colors.primary }} />
            <h3 className="text-2xl font-bold mb-4" style={{ color: theme.colors.text.primary }}>
              Professional Photography
            </h3>
            <p className="text-lg leading-relaxed" style={{ color: theme.colors.text.secondary }}>
              Capturing life's precious moments with artistic vision and technical excellence.
              Browse our galleries to find and purchase high-quality photos from your special
              events.
            </p>
          </div>
        </section>
      )}

      {error && (
        <div
          className="text-center py-8 px-4 rounded-lg"
          style={{
            backgroundColor: theme.colors.error + '10',
            color: theme.colors.error,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// Event Card Component
interface EventCardProps {
  event: GalleryEvent;
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const { theme } = useGalleryTheme();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Link to={`/events/${event.id}`} className="group block">
      <div
        className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:scale-105"
        style={{ backgroundColor: theme.colors.surface }}
      >
        {/* Cover Image */}
        <div className="aspect-video bg-gray-200 overflow-hidden">
          {event.coverImage ? (
            <img
              src={event.coverImage}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: theme.colors.background }}
            >
              <Camera size={48} style={{ color: theme.colors.text.muted }} />
            </div>
          )}
        </div>

        {/* Event Info */}
        <div className="p-6">
          <h3
            className="text-xl font-bold mb-2 group-hover:text-opacity-80 transition-colors"
            style={{ color: theme.colors.text.primary }}
          >
            {event.title}
          </h3>

          {event.description && (
            <p className="text-sm mb-4 line-clamp-2" style={{ color: theme.colors.text.secondary }}>
              {event.description}
            </p>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2" style={{ color: theme.colors.text.muted }}>
              <Calendar size={16} />
              <span>{formatDate(event.eventDate)}</span>
            </div>

            {event.location && (
              <div
                className="flex items-center space-x-2"
                style={{ color: theme.colors.text.muted }}
              >
                <MapPin size={16} />
                <span>{event.location}</span>
              </div>
            )}

            <div className="flex items-center space-x-2" style={{ color: theme.colors.text.muted }}>
              <Camera size={16} />
              <span>{event.totalPhotos} photos</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default GalleryHomePage;
